import type { CalculatorResult } from "./calculatorData";

export const overlapRowLimit = 20;
export type OverlapRow = { id: number; symbol: string; fund: string; direct: string };
export type OverlapScenario = { fundWeight: string; holdingsDate: string; rows: OverlapRow[] };
export function defaultOverlapScenario(): OverlapScenario {
  return { fundWeight: "50", holdingsDate: "", rows: [{ id: 0, symbol: "STOCK-A", fund: "6", direct: "10" }, { id: 1, symbol: "STOCK-B", fund: "4", direct: "0" }] };
}

const round = (value: number) => Math.round(value * 100) / 100;
export const overlapPercent = (value: number) => `${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
function percent(raw: string) {
  const value = Number(raw);
  return raw.trim() !== "" && Number.isFinite(value) && value >= 0 && value <= 100 ? round(value) : null;
}
function validDate(date: string) {
  if (date === "") return true;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const parsed = new Date(`${date}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date;
}

export function validateOverlap(scenario: OverlapScenario) {
  const fundWeight = percent(scenario.fundWeight);
  const fundError = fundWeight === null ? "Enter an ETF allocation from 0% to 100%." : null;
  const dateError = validDate(scenario.holdingsDate) ? null : "Enter a real holdings date or leave it blank if unknown.";
  const symbols = scenario.rows.map((row) => row.symbol.trim().toUpperCase());
  const errors = scenario.rows.map((row, index) => ({
    symbol: !/^[A-Z0-9][A-Z0-9.-]{0,19}$/.test(symbols[index]) ? "Use a ticker or identifier up to 20 characters (letters, numbers, dots or hyphens)." : symbols.indexOf(symbols[index]) !== symbols.lastIndexOf(symbols[index]) ? "Combine the same stock into one row." : null,
    fund: percent(row.fund) === null ? "Enter an ETF holding weight from 0% to 100%." : null,
    direct: percent(row.direct) === null ? "Enter a direct portfolio weight from 0% to 100%." : null,
  }));
  const invalidRows = !scenario.rows.length || scenario.rows.length > overlapRowLimit || errors.some((error) => error.symbol || error.fund || error.direct);
  if (fundError || dateError || invalidRows) return { fundError, dateError, errors, analysis: null, message: "Complete the highlighted fields. Enter 1 to 20 distinct stock rows." };
  const rows = scenario.rows.map((row, index) => {
    const fund = percent(row.fund)!;
    const direct = percent(row.direct)!;
    const throughFund = fundWeight! * fund / 100;
    return { symbol: symbols[index], fund, direct, throughFund, combined: direct + throughFund };
  });
  const coverage = rows.reduce((sum, row) => sum + row.fund, 0);
  const directTotal = rows.reduce((sum, row) => sum + row.direct, 0);
  if (coverage > 100 + 0.000001) return { fundError, dateError, errors, analysis: null, message: "Entered ETF holdings exceed 100%. Check the fund weights; they are not normalized." };
  if (fundWeight! + directTotal > 100 + 0.000001) return { fundError, dateError, errors, analysis: null, message: "ETF allocation plus direct stock weights exceeds 100% of the portfolio." };
  const largest = rows.reduce((best, row) => row.combined > best.combined ? row : best);
  const analysis = {
    rows, largest, coverage: Math.min(100, coverage), fundWeight: fundWeight!,
    unexaminedFund: fundWeight! * Math.max(0, 100 - coverage) / 100,
    otherPortfolio: Math.max(0, 100 - fundWeight! - directTotal),
    sharedCount: rows.filter((row) => row.direct > 0 && row.throughFund > 0).length,
  };
  return { fundError, dateError, errors, analysis, message: null };
}
export type OverlapAnalysis = NonNullable<ReturnType<typeof validateOverlap>["analysis"]>;
export function overlapResult(analysis: OverlapAnalysis): CalculatorResult {
  return {
    headlineLabel: "Largest examined stock exposure", headlineValue: overlapPercent(analysis.largest.combined), headlineDetail: analysis.largest.symbol,
    stats: [{ label: "Entered ETF holdings coverage", value: overlapPercent(analysis.coverage) }, { label: "Unexamined ETF exposure", value: overlapPercent(analysis.unexaminedFund), detail: "Share of the whole portfolio, not of the fund." }, { label: "Stocks held both ways", value: String(analysis.sharedCount) }],
    chart: { kind: "breakdown", valueLabel: "Entered exposure", segments: analysis.rows.map((row) => ({ label: row.symbol, value: row.combined, tone: "accent" })) },
    note: "One ETF plus direct stocks; partial coverage, not a diversification score",
  };
}
// Numeric catalog fields seed the fictional first row in crawler-readable examples.
export function calculateEtfOverlap(values: Record<string, number>): CalculatorResult {
  const scenario = { fundWeight: String(values.etfWeight), holdingsDate: "", rows: [{ id: 0, symbol: "STOCK-A", fund: String(values.stockInFund), direct: String(values.directStock) }] };
  const { analysis, message } = validateOverlap(scenario);
  if (!analysis) throw new RangeError(message ?? "Invalid overlap inputs.");
  return overlapResult(analysis);
}

export function overlapScenarioUrl(scenario: OverlapScenario, pageUrl: string) {
  if (!validateOverlap(scenario).analysis) throw new RangeError("Complete the scenario before sharing.");
  const url = new URL(pageUrl);
  url.search = "";
  const payload = { fundWeight: percent(scenario.fundWeight), holdingsDate: scenario.holdingsDate, rows: scenario.rows.map((row) => ({ symbol: row.symbol.trim().toUpperCase(), fund: percent(row.fund), direct: percent(row.direct) })) };
  url.hash = `scenario:${new URLSearchParams({ overlap: JSON.stringify(payload) })}`;
  return url.toString();
}
export function readOverlapScenario(hash: string): { scenario: OverlapScenario; notice: string | null } {
  const fallback = defaultOverlapScenario();
  if (!hash.startsWith("#scenario:")) return { scenario: fallback, notice: null };
  try {
    if (hash.length > 16_000) throw new RangeError();
    const raw: unknown = JSON.parse(new URLSearchParams(hash.slice(10)).get("overlap") ?? "null");
    if (!raw || typeof raw !== "object" || !("fundWeight" in raw) || typeof raw.fundWeight !== "number" || !("holdingsDate" in raw) || typeof raw.holdingsDate !== "string" || !("rows" in raw) || !Array.isArray(raw.rows) || raw.rows.length < 1 || raw.rows.length > overlapRowLimit) throw new RangeError();
    const rows = raw.rows.map((row: unknown, id) => {
      if (!row || typeof row !== "object" || !("symbol" in row) || typeof row.symbol !== "string" || !("fund" in row) || typeof row.fund !== "number" || !("direct" in row) || typeof row.direct !== "number") throw new RangeError();
      return { id, symbol: row.symbol, fund: String(row.fund), direct: String(row.direct) };
    });
    const scenario = { fundWeight: String(raw.fundWeight), holdingsDate: raw.holdingsDate, rows };
    if (!validateOverlap(scenario).analysis) throw new RangeError();
    return { scenario, notice: null };
  } catch {
    return { scenario: fallback, notice: "This shared scenario is invalid. The fictional example is shown instead." };
  }
}
